import { useEffect, useState } from 'react';
import { X } from 'lucide-react';

import { useCompanyStore } from '@/stores/company.store';

/** How long the banner stays up before hiding itself, in milliseconds. */
const AUTO_HIDE_MS = 6000;

function Notice({ companyName }: { companyName: string }) {
  const dismissNotice = useCompanyStore(state => state.dismissNotice);

  // Drives the countdown line: it starts full width and transitions to zero
  // over AUTO_HIDE_MS, so the bar itself is the timer.
  const [running, setRunning] = useState(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setRunning(true));
    const timer = setTimeout(dismissNotice, AUTO_HIDE_MS);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [dismissNotice]);

  return (
    <div className="bg-primary-10 border-primary-30 relative w-full border-b">
      <div className="flex items-center gap-3 px-6 py-3">
        <span className="bg-base-orange-40 size-3 flex-none rounded-full" />

        <p className="text-text-secondary flex-1 text-sm">
          Viewing <span className="font-bold">{companyName}</span> as
          superadmin. Every change is recorded against your account.
        </p>

        <button
          type="button"
          onClick={dismissNotice}
          aria-label="Close notification"
          className="text-base-orange-40 hover:bg-primary-20 flex-none cursor-pointer rounded-full p-1 transition-colors"
        >
          <X className="size-4" />
        </button>
      </div>

      <div className="bg-primary-20 absolute inset-x-0 bottom-0 h-0.5">
        <div
          className="bg-base-orange-40 h-full transition-[width] ease-linear"
          style={{
            width: running ? '0%' : '100%',
            transitionDuration: `${AUTO_HIDE_MS}ms`,
          }}
        />
      </div>
    </div>
  );
}

/**
 * Announcement shown at the top of the layout after switching company. Auto
 * hides once the countdown line underneath it runs out, or immediately when
 * the close button is used.
 */
function CompanySwitchBanner() {
  const notice = useCompanyStore(state => state.notice);

  if (!notice) return null;

  // Keyed on the notice id so a switch during a live banner restarts the timer.
  return <Notice key={notice.id} companyName={notice.companyName} />;
}

export { CompanySwitchBanner };
